import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { Recommendation } from '../types';
import { BarChart3, Sparkles, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';
import { MarketStats } from '../components/modern/MarketStats';
import { LiveTicker } from '../components/modern/LiveTicker';
import { SectorChart } from '../components/modern/SectorChart';
import { TopPerformers } from '../components/modern/TopPerformers';
import { StockCard } from '../components/modern/StockCard';
import { AutoCarousel } from '../components/modern/AutoCarousel';
import { DonutChart } from '../components/DonutChart';

export const Overview: React.FC = () => {
  const [recs, setRecs] = useState<Recommendation[]>([]);
  const [overviewData, setOverviewData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = async () => {
    try {
      setRefreshing(true);
      
      // Fetch overview data
      const overviewRes = await api.getOverviewStats();
      console.log('Overview API Response:', overviewRes);
      
      if (overviewRes.success && overviewRes.data) {
        setOverviewData(overviewRes.data);
        
        // Map recent recommendations from overview API
        if (overviewRes.data.recent_recommendations) {
          const mappedRecs = overviewRes.data.recent_recommendations.map((rec: any) => ({
            id: rec.id,
            stock: {
              id: rec.id,
              symbol: rec.company_name,
              name: rec.company_name,
              sector: rec.sector || 'Diversified',
              current_price: rec.current_price
            },
            brokerage: {
              id: rec.id,
              name: rec.organization_name,
              slug: rec.organization_name.toLowerCase().replace(/\s+/g, '-')
            },
            recommendation: rec.recommendation_type,
            potentialUpside: parseFloat(rec.upside_potential),
            potential_value: parseFloat(rec.upside_potential),
            targetPrice: rec.target_price,
            target_price: rec.target_price,
            current_price: rec.current_price,
            recommendation_type: rec.recommendation_type,
            upside_potential: parseFloat(rec.upside_potential),
            recommendation_date: rec.recommendation_date,
            pdf_url: rec.pdf_url
          }));
          
          // Fetch more recommendations for charts and sections
          const allRecsRes = await api.getRecommendations({ limit: 100 });
          if (allRecsRes.success) {
            const allMappedRecs = allRecsRes.data.map((rec: any) => ({
              ...rec,
              stock: rec.company ? {
                id: rec.company.id,
                symbol: rec.company.seo_name || rec.company.name,
                name: rec.company.name,
                sector: rec.company.sector || 'Diversified',
                current_price: rec.current_price
              } : null,
              brokerage: rec.organization ? {
                id: rec.organization.id,
                name: rec.organization.name,
                slug: rec.organization.seo_name
              } : null,
              recommendation: rec.recommendation_type,
              potentialUpside: rec.upside_potential,
              potential_value: rec.upside_potential,
              targetPrice: rec.target_price,
              target_price: rec.target_price,
              stock_score: rec.stock_score,
              stock_score_data: rec.stock_score_data
            }));
            setRecs(allMappedRecs);
          }
          
          console.log('Mapped Overview Recs:', mappedRecs.length);
        }
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const counts = useMemo(() => {
    // Use overview data if available, otherwise calculate from recs
    if (overviewData?.overview) {
      const overview = overviewData.overview;
      return {
        buy: parseInt(overview.buy_recommendations || '0'),
        sell: parseInt(overview.sell_recommendations || '0'),
        hold: parseInt(overview.hold_recommendations || '0'),
        total: parseInt(overview.total_recommendations || '0'),
        avgUpside: parseFloat(overview.avg_upside_potential || '0')
      };
    }
    
    // Fallback to calculating from recs
    const buy = recs.filter(r => r.recommendation === 'Buy').length;
    const sell = recs.filter(r => r.recommendation === 'Sell').length;
    const hold = recs.filter(r => r.recommendation === 'Hold').length;
    const avgUpside = recs.length ? (recs.reduce((s, r) => s + (r.potentialUpside || r.potential_value || 0), 0) / recs.length) : 0;
    return { buy, sell, hold, total: recs.length, avgUpside: Number(avgUpside.toFixed(2)) };
  }, [recs, overviewData]);

  const topGainers = useMemo(() => recs
    .slice()
    .filter(r => (r.potentialUpside || r.potential_value || 0) > 0)
    .sort((a,b) => (b.potentialUpside || b.potential_value || 0) - (a.potentialUpside || a.potential_value || 0))
    .slice(0, 5)
    .map((r, i) => ({
      symbol: r.stock?.symbol || 'N/A',
      name: r.stock?.name || 'N/A',
      upside: r.potentialUpside || r.potential_value || 0,
      rank: i + 1,
      sector: r.stock?.sector
    })), [recs]);

  const topLosers = useMemo(() => recs
    .slice()
    .sort((a,b) => (a.potentialUpside || a.potential_value || 0) - (b.potentialUpside || b.potential_value || 0))
    .slice(0, 5)
    .map((r, i) => ({
      symbol: r.stock?.symbol || 'N/A',
      name: r.stock?.name || 'N/A',
      upside: r.potentialUpside || r.potential_value || 0,
      rank: i + 1,
      sector: r.stock?.sector
    })), [recs]);

  const sectorBuckets = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of recs) {
      const s = r.stock?.sector || 'Diversified';
      map.set(s, (map.get(s) || 0) + 1);
    }
    return Array.from(map.entries()).map(([label, value]) => ({ label, value }));
  }, [recs]);

  const tickerItems = useMemo(() => {
    const items = recs.slice(0, 30).map(r => ({
      symbol: r.stock?.symbol || r.stock?.name || '—',
      brokerage: r.brokerage?.name || '—',
      recommendation: (r.recommendation || r.recommendation_type) as 'Buy' | 'Sell' | 'Hold',
      upside: r.potentialUpside || r.potential_value || r.upside_potential || 0
    }));
    console.log('Ticker Items:', items.length, items.slice(0, 3));
    return items;
  }, [recs]);

  const newRecommendations = useMemo(() => recs.slice(0, 6), [recs]);

  const highUpsideStocks = useMemo(() => recs
    .slice()
    .filter(r => r.recommendation === 'Buy' && (r.potentialUpside || r.potential_value || 0) >= 15)
    .sort((a,b) => (b.potentialUpside || b.potential_value || 0) - (a.potentialUpside || a.potential_value || 0))
    .slice(0, 6), [recs]);

  const highDownsideStocks = useMemo(() => recs
    .slice()
    .filter(r => r.recommendation === 'Sell' && (r.potentialUpside || r.potential_value || 0) < 0)
    .sort((a,b) => (a.potentialUpside || a.potential_value || 0) - (b.potentialUpside || b.potential_value || 0))
    .slice(0, 6), [recs]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-8">
            <div className="flex items-center justify-between">
              <div className="h-10 bg-gray-200 rounded-xl w-80"></div>
              <div className="h-10 bg-gray-200 rounded-xl w-32"></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {[...Array(5)].map((_, i) => <div key={i} className="h-32 bg-gray-200 rounded-2xl"></div>)}
            </div>
            <div className="h-24 bg-gray-200 rounded-2xl"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="h-96 bg-gray-200 rounded-2xl"></div>
              <div className="h-96 bg-gray-200 rounded-2xl"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Header Section with Gradient */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-700 rounded-3xl shadow-2xl p-8">
          <div className="absolute inset-0 bg-grid-white/10 bg-[size:20px_20px]" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl shadow-lg">
                  <BarChart3 className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-black text-white tracking-tight">
                    Market Overview
                  </h1>
                  <p className="text-blue-100 text-lg mt-1">Real-time insights from top brokerages</p>
                </div>
              </div>
            </div>
            
            <button
              onClick={loadData}
              disabled={refreshing}
              className="flex items-center gap-2 px-5 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-semibold rounded-xl transition-all duration-300 hover:scale-105 disabled:opacity-50"
            >
              <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Market Statistics */}
        <MarketStats stats={counts} />

        {/* Live Ticker */}
        <LiveTicker items={tickerItems} />

        {/* New Recommendations Section */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">New Recommendations</h2>
              <p className="text-gray-600">Latest analyst calls from top brokerages - Auto scrolling</p>
            </div>
          </div>
          
          <AutoCarousel autoPlaySpeed={4000} itemsPerView={3}>
            {newRecommendations.map((rec) => (
              <StockCard key={rec.id} recommendation={rec} variant="featured" />
            ))}
          </AutoCarousel>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recommendation Distribution */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg">
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Recommendation Distribution</h3>
                <p className="text-sm text-gray-500">Buy, Sell, and Hold distribution</p>
              </div>
            </div>
            
            <DonutChart
              segments={[
                { label: 'Buy', value: counts.buy, color: '#10b981' },
                { label: 'Hold', value: counts.hold, color: '#f59e0b' },
                { label: 'Sell', value: counts.sell, color: '#dc2626' },
              ]}
            />
            
            <div className="mt-6 grid grid-cols-3 gap-3">
              <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full shadow-md"></div>
                  <span className="text-sm font-semibold text-gray-700">Buy</span>
                </div>
                <div className="text-2xl font-bold text-green-600">{counts.buy}</div>
                <div className="text-xs text-green-600 mt-1">{((counts.buy/counts.total)*100).toFixed(0)}%</div>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl border border-yellow-200">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full shadow-md"></div>
                  <span className="text-sm font-semibold text-gray-700">Hold</span>
                </div>
                <div className="text-2xl font-bold text-yellow-600">{counts.hold}</div>
                <div className="text-xs text-yellow-600 mt-1">{((counts.hold/counts.total)*100).toFixed(0)}%</div>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-red-50 to-rose-50 rounded-xl border border-red-200">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full shadow-md"></div>
                  <span className="text-sm font-semibold text-gray-700">Sell</span>
                </div>
                <div className="text-2xl font-bold text-red-600">{counts.sell}</div>
                <div className="text-xs text-red-600 mt-1">{((counts.sell/counts.total)*100).toFixed(0)}%</div>
              </div>
            </div>
          </div>

          {/* Sector Distribution */}
          <SectorChart data={sectorBuckets} total={counts.total} />
        </div>

        {/* High Upside Stocks */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">High Upside Stocks</h2>
              <p className="text-gray-600">Stocks with 15%+ potential upside</p>
            </div>
          </div>
          
          {highUpsideStocks.length > 0 ? (
            <AutoCarousel autoPlaySpeed={4500} itemsPerView={3}>
              {highUpsideStocks.map((rec) => (
                <StockCard key={rec.id} recommendation={rec} variant="featured" />
              ))}
            </AutoCarousel>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
              <TrendingUp className="h-12 w-12 text-green-400 mx-auto mb-3" />
              <p className="text-green-700 font-medium">No high upside stocks available at the moment</p>
            </div>
          )}
        </div>

        {/* High Downside Stocks */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl shadow-lg">
              <TrendingDown className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">High Downside Stocks</h2>
              <p className="text-gray-600">Stocks with sell recommendations and downside risk</p>
            </div>
          </div>
          
          {highDownsideStocks.length > 0 ? (
            <AutoCarousel autoPlaySpeed={4500} itemsPerView={3}>
              {highDownsideStocks.map((rec) => (
                <StockCard key={rec.id} recommendation={rec} variant="featured" />
              ))}
            </AutoCarousel>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
              <TrendingDown className="h-12 w-12 text-red-400 mx-auto mb-3" />
              <p className="text-red-700 font-medium">No high downside stocks available at the moment</p>
            </div>
          )}
        </div>

        {/* Top Performers */}
        <TopPerformers gainers={topGainers} losers={topLosers} />

      </div>
    </div>
  );
};